import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Checkbox, Input, Layout, notification, Select, Typography } from 'antd';
import {
  CalendarOutlined, CaretDownFilled, CaretUpFilled, CommentOutlined,
  PaperClipOutlined, RedoOutlined, SearchOutlined, TagOutlined
} from '@ant-design/icons';
import IncidentModal from './IncidentModal';
import { SkelLines, SkelList } from './Skeleton';
import LoginRequired from './LoginRequired';
import { STATUS_META, STATUS_OPTIONS as ALL_STATUS_OPTIONS } from './statusMeta';

const STATUS_OPTIONS = [{ value: '', label: 'Všetky stavy' }, ...ALL_STATUS_OPTIONS];

const PRIORITY_META = {
  1: { label: 'URGENT', color: '#ff5a5a' },
  2: { label: 'NORMAL', color: '#646060' },
};

const MIN_LOAD_MS = 1500;

const afterMinDelay = (startedAt, fetchId, currentIdRef, done) => {
  const wait = Math.max(0, MIN_LOAD_MS - (Date.now() - startedAt));
  setTimeout(() => {
    if (fetchId === currentIdRef.current) done();
  }, wait);
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: String(status), color: '#888', bg: '#222' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
      color: m.color, background: m.bg, border: `1px solid ${m.color}33`,
    }}>
      {m.label}
    </span>
  );
};

const CategoryItem = ({ cat, checked, onToggle, closed, index = 0 }) => {
  const openCount = Number(cat.status1 || 0) + Number(cat.status2 || 0) + Number(cat.status5 || 0) + Number(cat.status6 || 0);
  const count = closed ? Number(cat.total || 0) : openCount;

  return (
    <div
      className={`proj-list-item stagger-in${checked ? ' active' : ''}`}
      style={{ '--i': Math.min(index, 24) }}
      onClick={() => onToggle(cat.category)}
    >
      <Checkbox
        className="proj-list-check"
        checked={checked}
        style={{ pointerEvents: 'none' }}
      />
      <div className="proj-list-name">
        <span className="proj-list-name-text">{cat.category}</span>
        <span className="proj-cat-count-small">{count}</span>
      </div>
    </div>
  );
};

const SortIcon = ({ field, sortField, sortOrder }) => {
  if (sortField !== field) return <CaretDownFilled style={{ opacity: 0.2, fontSize: 10, marginLeft: 3 }} />;
  return sortOrder === 'asc'
    ? <CaretUpFilled   style={{ color: '#00b96b', fontSize: 10, marginLeft: 3 }} />
    : <CaretDownFilled style={{ color: '#00b96b', fontSize: 10, marginLeft: 3 }} />;
};

const Systems = ({ loggedInUser }) => {
  const [categoryStats, setCategoryStats] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');

  const [categorySearch, setCategorySearch] = useState('');
  const [incidentSearch, setIncidentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const categoriesFetchId = useRef(0);
  const incidentsFetchId = useRef(0);
  const [catAnimKey, setCatAnimKey] = useState(0);
  const [incAnimKey, setIncAnimKey] = useState(0);
  const wasCatLoading = useRef(true);
  const wasIncLoading = useRef(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  const PAGE_SIZE = 40;
  const showCategoryCol = selectedCategories.length > 1;
  const colCount = showCategoryCol ? 11 : 10;

  useEffect(() => {
    if (wasCatLoading.current && !loadingCategories) {
      setCatAnimKey(k => k + 1);
    }
    wasCatLoading.current = loadingCategories;
  }, [loadingCategories]);

  useEffect(() => {
    if (wasIncLoading.current && !loadingIncidents) {
      setIncAnimKey(k => k + 1);
    }
    wasIncLoading.current = loadingIncidents;
  }, [loadingIncidents]);

  useEffect(() => {
    if (!loggedInUser) return;
    axios.get(`${process.env.REACT_APP_API_URL}/companies`)
      .then(r => setCompanies(r.data || []))
      .catch(() => {});
  }, [loggedInUser]);

  const fetchCategoryStats = (company = companyFilter) => {
    if (!loggedInUser) return;
    setLoadingCategories(true);
    const fetchId = ++categoriesFetchId.current;
    const startedAt = Date.now();
    axios.get(`${process.env.REACT_APP_API_URL}/category-stats`, {
      params: { company: company || undefined },
    })
      .then(r => {
        if (fetchId !== categoriesFetchId.current) return;
        setCategoryStats(r.data || []);
      })
      .catch(() => {
        if (fetchId !== categoriesFetchId.current) return;
        notification.error({ message: 'Chyba pri načítaní kategórií.' });
      })
      .finally(() => {
        afterMinDelay(startedAt, fetchId, categoriesFetchId, () => setLoadingCategories(false));
      });
  };

  const fetchIncidents = (list, pg = 1, status = '', search = '', company = companyFilter, sf = sortField, so = sortOrder) => {
    if (!list || list.length === 0) return;
    setLoadingIncidents(true);
    const fetchId = ++incidentsFetchId.current;
    const startedAt = Date.now();
    axios.get(`${process.env.REACT_APP_API_URL}/incidents`, {
      params: {
        categories: list.join(','),
        page: pg,
        pageSize: PAGE_SIZE,
        status: status || undefined,
        search: search || undefined,
        company: company || undefined,
        sortField: sf,
        sortOrder: so,
      },
    })
      .then(r => {
        if (fetchId !== incidentsFetchId.current) return;
        setIncidents(r.data.data || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => {
        if (fetchId !== incidentsFetchId.current) return;
        notification.error({ message: 'Chyba pri načítaní incidentov.' });
      })
      .finally(() => {
        afterMinDelay(startedAt, fetchId, incidentsFetchId, () => setLoadingIncidents(false));
      });
  };

  useEffect(() => {
    fetchCategoryStats();
  }, [loggedInUser]);

  const handleCompanyChange = (val) => {
    const company = val || '';
    setCompanyFilter(company);
    setSelectedCategories([]);
    setIncidents([]);
    setTotal(0);
    setPage(1);
    setStatusFilter('');
    setIncidentSearch('');
    fetchCategoryStats(company);
  };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => {
      const next = prev.includes(cat)
        ? prev.filter(c => c !== cat)
        : [...prev, cat];
      setPage(1);
      setStatusFilter('');
      setIncidentSearch('');
      if (next.length === 0) {
        setIncidents([]);
        setTotal(0);
      } else {
        fetchIncidents(next, 1, '', '', companyFilter);
      }
      return next;
    });
  };

  const clearCategories = () => {
    setSelectedCategories([]);
    setIncidents([]);
    setTotal(0);
    setPage(1);
    setStatusFilter('');
    setIncidentSearch('');
  };

  const selectVisibleCategories = (names) => {
    setSelectedCategories(names);
    setPage(1);
    setStatusFilter('');
    setIncidentSearch('');
    if (names.length === 0) {
      setIncidents([]);
      setTotal(0);
    } else {
      fetchIncidents(names, 1, '', '', companyFilter);
    }
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(1);
    fetchIncidents(selectedCategories, 1, val, incidentSearch, companyFilter);
  };

  const handleSearchChange = (val) => {
    setIncidentSearch(val);
    setPage(1);
    fetchIncidents(selectedCategories, 1, statusFilter, val, companyFilter);
  };

  const handleRowClick = (id) => {
    axios.get(`${process.env.REACT_APP_API_URL}/incident/get/${id}`)
      .then(r => { setSelectedIncident(r.data); setModalOpen(true); })
      .catch(() => notification.error({ message: 'Chyba pri načítaní incidentu.' }));
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedIncident(null);
    if (selectedCategories.length > 0) {
      fetchIncidents(selectedCategories, page, statusFilter, incidentSearch, companyFilter, sortField, sortOrder);
    }
  };

  const { openCategories, closedCategories, visibleNames } = useMemo(() => {
    const t = categorySearch.trim().toLowerCase();
    const filtered = t
      ? categoryStats.filter(c => c.category.toLowerCase().includes(t))
      : categoryStats;

    const byName = (a, b) => (a.category || '').localeCompare(b.category || '', 'sk');
    const open = filtered
      .filter(c => Number(c.status1) > 0 || Number(c.status2) > 0 || Number(c.status5) > 0 || Number(c.status6) > 0)
      .sort(byName);
    const closed = filtered
      .filter(c => Number(c.status1) === 0 && Number(c.status2) === 0 && Number(c.status5) === 0 && Number(c.status6) === 0)
      .sort(byName);
    return {
      openCategories: open,
      closedCategories: closed,
      visibleNames: [...open, ...closed].map(c => c.category),
    };
  }, [categoryStats, categorySearch]);

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    setPage(1);
    fetchIncidents(selectedCategories, 1, statusFilter, incidentSearch, companyFilter, field, newOrder);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const handlePageChange = (pg) => {
    setPage(pg);
    fetchIncidents(selectedCategories, pg, statusFilter, incidentSearch, companyFilter, sortField, sortOrder);
  };

  const topbarLabel = selectedCategories.length === 1
    ? selectedCategories[0]
    : selectedCategories.length > 1
      ? `${selectedCategories.length} kategórií`
      : null;

  const tableColumns = [
    { key: 'niceid',        label: 'ID',          w: 120 },
    ...(showCategoryCol ? [{ key: 'category', label: 'Kategória', w: 140 }] : []),
    { key: 'text',          label: 'Popis',       w: null },
    { key: 'priority',      label: 'Priorita',    w: 100 },
    { key: 'status',        label: 'Stav',        w: 130 },
    { key: 'email',         label: 'Email',       w: 190 },
    { key: 'created',       label: 'Vytvorené',   w: 100 },
    { key: 'dueDate',       label: 'Due date',    w: 100 },
    { key: 'plannedEffort', label: 'MD (N)',      w: 70  },
    { key: 'actualEffort',  label: 'MD (R)',      w: 70  },
    { key: 'lastUpdate',    label: 'Posl. zmena', w: 120 },
  ];

  if (!loggedInUser) {
    return (
      <Layout style={{ padding: '24px 16px', minHeight: 360, width: '100%' }}>
        <LoginRequired
          title="Prihláste sa pre prehľad systémov"
          subtitle="Prehľad podľa kategórií a systémov je dostupný až po prihlásení firemným účtom."
        />
      </Layout>
    );
  }

  let catIndex = 0;

  return (
    <Layout style={{ padding: 0, width: '100%' }} className="projects-page">

      <div className="proj-topbar">
        <div className="proj-topbar-left">
          <TagOutlined style={{ fontSize: 16, color: '#00b96b', marginRight: 8 }} />
          <span className="proj-topbar-title">Prehľad podľa systémov</span>
          {topbarLabel && (
            <>
              <span className="proj-topbar-sep">/</span>
              <span className="proj-topbar-project">{topbarLabel}</span>
              <span className="proj-topbar-sep" style={{ fontSize: 12, color: '#555' }}>
                {total} požiadaviek
              </span>
            </>
          )}
        </div>
        {loggedInUser?.role !== 'USER' && (
          <Select
            style={{ width: 220 }}
            placeholder="Všetky spoločnosti"
            allowClear
            value={companyFilter || undefined}
            onChange={handleCompanyChange}
            options={[
              ...companies.map(c => ({ value: c.alias, label: `${c.name} (${c.alias})` }))
            ]}
            showSearch
            optionFilterProp="label"
          />
        )}
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        <div className="proj-sidebar">
          <div className="proj-sidebar-search">
            <Input
              prefix={<SearchOutlined style={{ color: '#555' }} />}
              placeholder="Hľadať kategóriu..."
              value={categorySearch}
              onChange={e => setCategorySearch(e.target.value)}
              size="small"
            />
            <div className="proj-sidebar-actions">
              <button
                type="button"
                className="proj-sidebar-action"
                onClick={() => selectVisibleCategories(visibleNames)}
                disabled={visibleNames.length === 0}
              >
                Označiť viditeľné
              </button>
              <button
                type="button"
                className="proj-sidebar-action"
                onClick={clearCategories}
                disabled={selectedCategories.length === 0}
              >
                Zrušiť výber ({selectedCategories.length})
              </button>
            </div>
          </div>

          <div className="proj-list-wrap">
            {loadingCategories ? (
              <div style={{ padding: '8px 10px' }}><SkelLines count={10} /></div>
            ) : (openCategories.length === 0 && closedCategories.length === 0) ? (
              <div className="proj-list-empty">Žiadne kategórie</div>
            ) : (
              <div className="stagger-wrap" key={catAnimKey}>
                {openCategories.length > 0 && (
                  <>
                    <div className="proj-group-label proj-group-open stagger-in" style={{ '--i': catIndex++ }}>
                      OTVORENÉ BODY
                      <span className="proj-group-count">{openCategories.length}</span>
                    </div>
                    {openCategories.map(cat => (
                      <CategoryItem
                        key={cat.category}
                        cat={cat}
                        checked={selectedCategories.includes(cat.category)}
                        onToggle={toggleCategory}
                        index={catIndex++}
                      />
                    ))}
                  </>
                )}
                {closedCategories.length > 0 && (
                  <>
                    <div className="proj-group-label proj-group-closed stagger-in" style={{ '--i': catIndex++ }}>
                      UZAVRETÉ PROJEKTY
                      <span className="proj-group-count">{closedCategories.length}</span>
                    </div>
                    {closedCategories.map(cat => (
                      <CategoryItem
                        key={cat.category}
                        cat={cat}
                        checked={selectedCategories.includes(cat.category)}
                        onToggle={toggleCategory}
                        closed
                        index={catIndex++}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="proj-content">
          {selectedCategories.length === 0 ? (
            <div className="proj-empty-state">
              <TagOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
              <Typography.Text style={{ color: '#555', marginTop: 12 }}>
                Zaškrtnite jednu alebo viac kategórií v zozname
              </Typography.Text>
            </div>
          ) : (
            <>
              <div className="proj-filter-bar">
                <Input
                  prefix={<SearchOutlined style={{ color: '#555' }} />}
                  placeholder="Hľadať v požiadavkách..."
                  value={incidentSearch}
                  onChange={e => handleSearchChange(e.target.value)}
                  style={{ width: 260 }}
                  allowClear
                />
                <Select
                  style={{ width: 180 }}
                  value={statusFilter}
                  onChange={handleStatusChange}
                  options={STATUS_OPTIONS}
                />
                <span className="proj-count-label">
                  <strong>{total}</strong> požiadaviek
                </span>
              </div>

              <div className="proj-table-wrap">
                <table className="proj-table">
                  <thead>
                    <tr>
                      {tableColumns.map(col => (
                        <th
                          key={col.key}
                          style={{ width: col.w || undefined, cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          <SortIcon field={col.key} sortField={sortField} sortOrder={sortOrder} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingIncidents ? (
                      <tr>
                        <td colSpan={colCount} style={{ padding: '12px 10px' }}>
                          <SkelList rows={10} />
                        </td>
                      </tr>
                    ) : incidents.length === 0 ? (
                      <tr><td colSpan={colCount} className="proj-table-empty">Žiadne požiadavky</td></tr>
                    ) : (() => {
                      const GROUP_ORDER = [2, 6, 1, 4, 7, 3, 5];
                      const groups = GROUP_ORDER.reduce((acc, s) => {
                        const rows = incidents.filter(i => Number(i.status) === s);
                        if (rows.length) acc.push({ status: s, rows });
                        return acc;
                      }, []);
                      let rowI = 0;
                      return (
                        <React.Fragment key={incAnimKey}>
                          {groups.map(({ status, rows }) => {
                            const groupI = rowI++;
                            return [
                              <tr
                                key={`grp-${status}`}
                                className="proj-table-group-row stagger-in"
                                style={{ '--i': Math.min(groupI, 24) }}
                              >
                                <td colSpan={colCount}>
                                  <span className="proj-table-group-label" style={{ color: STATUS_META[status]?.color }}>
                                    {STATUS_META[status]?.label}
                                  </span>
                                  <span className="proj-table-group-count">{rows.length}</span>
                                </td>
                              </tr>,
                              ...rows.map(inc => {
                                const idx = rowI++;
                                const priority = PRIORITY_META[inc.priority] || PRIORITY_META[2];
                                return (
                                  <tr
                                    key={inc.id}
                                    className="proj-table-row stagger-in"
                                    style={{ '--i': Math.min(idx, 24) }}
                                    onClick={() => handleRowClick(inc.id)}
                                  >
                                    <td><span className="proj-niceid">{inc.niceid}</span></td>
                                    {showCategoryCol && (
                                      <td>
                                        <span className="proj-meta-text" title={inc.category}>{inc.category || '—'}</span>
                                      </td>
                                    )}
                                    <td>
                                      <span className="proj-text">{inc.text}</span>
                                      {(Number(inc.attachmentCount) > 0 || Number(inc.commentCount) > 0) && (
                                        <span className="proj-row-icons">
                                          {Number(inc.attachmentCount) > 0 && (
                                            <PaperClipOutlined
                                              className="proj-row-icon"
                                              title="Príloha"
                                            />
                                          )}
                                          {Number(inc.commentCount) > 0 && (
                                            <CommentOutlined
                                              className="proj-row-icon"
                                              title={`${inc.commentCount} komentárov`}
                                            />
                                          )}
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      <span style={{ color: priority.color, fontSize: 11, fontWeight: 700 }}>
                                        {priority.label}
                                      </span>
                                    </td>
                                    <td><StatusBadge status={inc.status} /></td>
                                    <td>
                                      <span className="proj-meta-text" title={inc.email}>{inc.email || '—'}</span>
                                    </td>
                                    <td>
                                      <span className="proj-meta-text">
                                        {inc.created ? moment(inc.created).format('DD.MM.YY') : '—'}
                                      </span>
                                    </td>
                                    <td>
                                      {inc.dueDate ? (
                                        <span className="proj-meta-text">
                                          <CalendarOutlined style={{ marginRight: 4, color: '#888' }} />
                                          {moment(inc.dueDate).format('DD.MM.YY')}
                                        </span>
                                      ) : <span className="proj-meta-text">—</span>}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className="proj-meta-text" style={{ color: inc.plannedEffort ? '#a3d4ff' : '#444' }}>
                                        {inc.plannedEffort != null && inc.plannedEffort !== '' ? inc.plannedEffort : '—'}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className="proj-meta-text" style={{ color: inc.actualEffort ? '#90e8b0' : '#444' }}>
                                        {inc.actualEffort != null && inc.actualEffort !== '' ? inc.actualEffort : '—'}
                                      </span>
                                    </td>
                                    <td>
                                      {inc.lastUpdate ? (
                                        <span className="proj-meta-text">
                                          <RedoOutlined style={{ marginRight: 4, color: '#888' }} />
                                          {moment(inc.lastUpdate).format('DD.MM. HH:mm')}
                                        </span>
                                      ) : <span className="proj-meta-text">—</span>}
                                    </td>
                                  </tr>
                                );
                              }),
                            ];
                          })}
                        </React.Fragment>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {!loadingIncidents && totalPages > 1 && (
                <div className="proj-pagination">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                    <button
                      key={pg}
                      className={`proj-page-btn${page === pg ? ' active' : ''}`}
                      onClick={() => handlePageChange(pg)}
                    >
                      {pg}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <IncidentModal
        open={modalOpen}
        incident={selectedIncident}
        onClose={handleModalClose}
      />
    </Layout>
  );
};

export default Systems;
